import { gql, useMutation } from '@apollo/client';
import moment from 'moment';
import React from 'react';
import { useDispatch } from 'react-redux';
import Form, { Field, Submit } from '../components/Form';
import Modal, { ModalActionFooter, ModalTitle } from '../components/Modal';
import USER_AVATAR_FRAGMENT from '../graphql/fragments/UserAvatar';
import type {
  CreateVerificationModal_PostFragment,
  CreateVerificationMutation,
  CreateVerificationMutationVariables,
} from '../graphql/generated/graphqlTypes';
import UIActions from '../ui/UIActions';
import yearSensitiveDateString from '../utils/yearSensitiveDateString';
import styles from './CreateVerificationModal.module.scss';
import { calculateExpirationTimestamp } from './verificationDateHelpers';
import type { VerificationFormValues } from './VerificationFrequencyFields';
import VerificationFrequencyFields, {
  FREQUENCIES,
  validateVerificationFrequencyFields,
} from './VerificationFrequencyFields';
import VerifiedIcon from './VerifiedIcon';
import VerifiedToast from './VerifiedToast';

const CREATE_VERIFICATION_MUTATION = gql`
  mutation createVerification(
    $postId: ID!
    $unit: VerificationUnit!
    $quantity: Int!
  ) {
    verification: createVerification(
      postId: $postId
      unit: $unit
      quantity: $quantity
    ) {
      id
      post {
        id
        verifiedAt
        verification {
          id
          unit
          quantity
          insertedAt
        }
        verificationEntries {
          id
          verifiedAt
          version
          expiredAt
          verifier {
            id
            name
            title
            ...UserAvatar
          }
        }
      }
    }
  }
  ${USER_AVATAR_FRAGMENT}
`;

const POST_FRAGMENT = gql`
  fragment CreateVerificationModal_post on Post {
    id
  }
`;

interface CreateVerificationModalProps {
  post: CreateVerificationModal_PostFragment;
  close: () => void;
}

type FormValues = VerificationFormValues & {
  confirm: boolean;
};

const CreateVerificationModal = ({
  post,
  close,
}: CreateVerificationModalProps) => {
  const dispatch = useDispatch();
  const [createVerification] = useMutation<
    CreateVerificationMutation,
    CreateVerificationMutationVariables
  >(CREATE_VERIFICATION_MUTATION);

  const onSubmit = async ({ unit, quantity: quantityString }: FormValues) => {
    const quantity = Number(quantityString);
    if (!unit) {
      return null;
    }
    return createVerification({
      variables: { unit, quantity, postId: post.id },
    }).then(() => {
      close();
      dispatch(
        UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
          <VerifiedToast
            onDismiss={toastProps.onDismiss}
            unit={unit}
            quantity={quantity}
          />
        )),
      );
    });
  };

  return (
    <Modal width={515}>
      <ModalTitle
        subtitle="Post owners must periodically review verified posts so that the post retains verified status."
        close={close}
      >
        Verify post <VerifiedIcon inline size="large" />
      </ModalTitle>
      <Form<FormValues>
        onSubmit={onSubmit}
        initialValues={{
          ...FREQUENCIES[0],
          confirm: false,
        }}
        validate={validateVerificationFrequencyFields}
      >
        {({ values, setFieldValue, errors, isValid, dirty }) => (
          <>
            <VerificationFrequencyFields
              frequency={values.frequency}
              setFieldValue={setFieldValue}
            />
            {(Object.keys(errors).length === 0 ||
              (Object.keys(errors).length === 1 &&
                Object.keys(errors).includes('confirm'))) && (
              <div className={styles.notice}>
                We’ll notify you to reverify this post on{' '}
                {yearSensitiveDateString(
                  calculateExpirationTimestamp(moment().valueOf(), {
                    unit: values.unit,
                    quantity: Number(values.quantity),
                  }),
                )}
                .
              </div>
            )}
            <hr className={styles.hr} />
            <label>
              <div className={styles.label}>Confirm</div>
              <Field
                name="confirm"
                label="I’ve reviewed this post for accuracy"
                type="checkbox"
                required
              />
            </label>
            <ModalActionFooter
              primary={
                <Submit size="large" disabled={!dirty || !isValid}>
                  Verify
                </Submit>
              }
            />
          </>
        )}
      </Form>
    </Modal>
  );
};

CreateVerificationModal.fragments = { post: POST_FRAGMENT };

export default CreateVerificationModal;
